import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';
import { useDisclosure } from '@chakra-ui/hooks';
import { useSKU } from 'hooks/useSKU';
import { useAppSelector } from 'store/hooks';
import { washhubSelector } from 'store/slices/washhub';
import { deleteQALog, updateQALog } from 'api/QALog';
import { useMutation, useQueryClient } from 'react-query';
import { successToast } from 'utils/successToast';
import {
  EditRefType,
  EditQALogModalProps,
  QAItem,
} from './EditQALogModal.props';
import EditQALogModalView from './EditQALogModal.view';
import { washHub } from 'store/slices';
import { toApiPayloadDateFormat } from 'utils/dateUtils';
import { QALog } from 'types/QALog';

const EditQALogModal = forwardRef<EditRefType, EditQALogModalProps>(
  (_, ref) => {
    const queryClient = useQueryClient();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [qALog, setQALog] = useState<QALog | null>(null);

    const washHubs = useAppSelector(washhubSelector);
    const washHubId = washHub.selectors.useSelectedWashHubId();
    const { data: skus = [], isLoading: isLoadingSKU } = useSKU(
      'item',
      washHubId,
    );
    const { mutate: deleteQALogMutate, isLoading: isDeleting } = useMutation(
      deleteQALog,
    );

    const { mutate, isLoading: isUpdating } = useMutation(updateQALog, {
      onSuccess: () => {
        successToast('QA Log updated successfully');
        queryClient.invalidateQueries({
          queryKey: ['fetch-qa-log'],
        });
        onClose();
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        onOpen: (qALog: QALog) => {
          setQALog(qALog);
          onOpen();
        },
      }),
      [onOpen],
    );

    const onSubmit = (values: any) => {
      const { employee, date, hours, items, notes } = values || {};

      const updatedItems = items.map((item: QAItem) => ({
        sku: item.sku,
        ...(item.passed !== undefined && { passed: Number(item.passed) }),
        ...(item.dirty !== undefined && { dirty: Number(item.dirty) }),
        ...(item.damaged !== undefined && { damaged: Number(item.damaged) }),
        ...(item.total !== undefined && { total: Number(item.total) }),
        ...(item.yield !== undefined && { yield: Number(item.yield) }),
      }));

      const payload = {
        _id: qALog?._id || '',
        employee,
        date: toApiPayloadDateFormat(date),
        hours: Number(hours),
        items: updatedItems,
        notes,
      };

      mutate(payload);
    };

    const onDelete = () =>
      deleteQALogMutate(qALog?._id, {
        onSuccess: () => {
          successToast('QA log Deleted');
          queryClient.invalidateQueries({
            queryKey: ['fetch-qa-log'],
          });
          onClose();
        },
      });

    const isLoading = isUpdating || isDeleting;

    const modalProps = {
      qALog,
      skus,
      isOpen,
      onClose,
      onSubmit,
      onDelete,
      washHubs,
      isLoading,
      isLoadingSKU,
    };

    return <EditQALogModalView {...modalProps} />;
  },
);

export default memo(EditQALogModal);
